import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SettingsPhoneIcon from "@mui/icons-material/SettingsPhone";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getRequestWithToken, patchRequestWithToken, postRequest, deleteRequest } from "../../utils/ApiRequests";
import { GlobalContext } from "../../components/GlobalStateProvide/GlobalStateProvider";
import Unauthorized from "../../components/unauthorized/Unauthorized";

const api = "admin";

const emptyCredentialForm = {
  name: "",
  obd_url: "https://obd-api.myoperator.co/obd-api-v1",
  search_url: "https://developers.myoperator.co/search/search",
  recording_url: "https://developers.myoperator.co/search/recordings/link",
  api_key: "",
  api_token: "",
  public_api_token: "",
  secret_token: "",
  company_id: "",
  public_ivr_id: "",
  admin_number: "",
  default_user_id: "",
  webhook_secret: "",
  notes: "",
  is_active: true,
};

const MyOperatorSettings = () => {
  const { handleSnackBar } = useContext(GlobalContext);
  const resourceNames = JSON.parse(localStorage.getItem("resourceNames") || "[]");
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [configForm, setConfigForm] = useState(emptyCredentialForm);

  const isSuperAdmin = Boolean(profile?.is_super_admin);
  const canViewSettings = isSuperAdmin || resourceNames.includes("MyOperator Settings");

  const loadData = async () => {
    setLoading(true);
    try {
      const meRes = await getRequestWithToken(`${api}/me`);
      const me = meRes.data?.data || null;
      setProfile(me);

      if (me?.is_super_admin) {
        const configRes = await getRequestWithToken(`${api}/myoperator-configs`);
        setConfigs(configRes.data?.data || []);
      } else {
        setConfigs([]);
      }
    } catch (err) {
      handleSnackBar(true, err?.response?.data?.msg || err?.message || "Failed to load MyOperator settings", "error");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const activeConfig = useMemo(
    () => configs.find((item) => item.is_active && !item.deleted?.is_deleted) || null,
    [configs]
  );

  const openCreateDialog = () => {
    setEditingConfig(null);
    setConfigForm(emptyCredentialForm);
    setConfigDialogOpen(true);
  };

  const openEditDialog = (config) => {
    setEditingConfig(config);
    setConfigForm({
      ...emptyCredentialForm,
      ...config,
    });
    setConfigDialogOpen(true);
  };

  const handleSaveConfig = async () => {
    setConfigSaving(true);
    try {
      const payload = { ...configForm };
      const res = editingConfig
        ? await patchRequestWithToken(`${api}/myoperator-configs/${editingConfig._id}`, payload)
        : await postRequest(`${api}/myoperator-configs`, payload);
      handleSnackBar(true, res.data?.msg || "Credentials saved", "success");
      setConfigDialogOpen(false);
      await loadData();
    } catch (err) {
      handleSnackBar(true, err?.response?.data?.msg || err?.message || "Failed to save credentials", "error");
    }
    setConfigSaving(false);
  };

  const handleDeleteConfig = async (configId) => {
    try {
      const res = await deleteRequest(`${api}/myoperator-configs/${configId}`);
      handleSnackBar(true, res.data?.msg || "Credentials deleted", "success");
      await loadData();
    } catch (err) {
      handleSnackBar(true, err?.response?.data?.msg || err?.message || "Failed to delete credentials", "error");
    }
  };

  const handleActivateConfig = async (config) => {
    try {
      const res = await patchRequestWithToken(`${api}/myoperator-configs/${config._id}`, {
        ...config,
        is_active: true,
      });
      handleSnackBar(true, res.data?.msg || "Credentials activated", "success");
      await loadData();
    } catch (err) {
      handleSnackBar(true, err?.response?.data?.msg || err?.message || "Failed to activate credentials", "error");
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "50vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canViewSettings) {
    return <Unauthorized />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: "grid", gap: 3 }}>
      <Card sx={{ borderRadius: 4 }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ md: "center" }}>
            <Box>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <SettingsPhoneIcon color="primary" />
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  MyOperator Setup
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ mt: 1, color: "text.secondary", maxWidth: 760 }}>
                Your call-center profile now uses MyOperator as the source for syncing calls and contacts. Save each admin's MyOperator user ID here, and keep provider credentials in the database so super-admins can update them without code changes.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Chip
                color={isSuperAdmin ? "success" : "default"}
                icon={isSuperAdmin ? <CheckCircleIcon /> : undefined}
                label={isSuperAdmin ? "Super Admin Access" : "User Access"}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={5}>
            <Card sx={{ borderRadius: 4, height: "100%" }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Assigned MyOperator User ID
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75, color: "text.secondary" }}>
                This user ID is used while placing calls from the admin panel. Only super-admin can assign or change it.
                </Typography>

                <TextField
                  fullWidth
                  label="MyOperator User ID"
                  value={profile?.myoperator_user_id || ""}
                  InputProps={{ readOnly: true }}
                  sx={{ mt: 3 }}
                />

                {!isSuperAdmin && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Contact a super-admin to assign or update your MyOperator user ID.
                  </Alert>
                )}
              </CardContent>
            </Card>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Card sx={{ borderRadius: 4, height: "100%" }}>
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} justifyContent="space-between" alignItems={{ sm: "center" }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    MyOperator Credentials
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.75, color: "text.secondary" }}>
                    Only super-admin can create, edit, activate, or remove the provider URLs and credentials used by calling, call sync, and recordings.
                  </Typography>
                </Box>
                {isSuperAdmin && (
                  <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
                    Add Credentials
                  </Button>
                )}
              </Stack>

              {!isSuperAdmin && (
                <Alert severity="info" sx={{ mt: 2.5 }}>
                  Credential management is locked to super-admin accounts.
                </Alert>
              )}

              {isSuperAdmin && (
                <Stack spacing={2} sx={{ mt: 2.5 }}>
                  {configs.length === 0 && (
                    <Alert severity="warning">No MyOperator credential record has been saved yet.</Alert>
                  )}

                  {configs.map((config) => (
                    <Card key={config._id} variant="outlined" sx={{ borderRadius: 3 }}>
                      <CardContent>
                        <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {config.name}
                              </Typography>
                              {config.is_active && <Chip size="small" color="success" label="Active" />}
                            </Stack>
                            <Typography variant="body2" sx={{ mt: 0.8, color: "text.secondary" }}>
                              Company ID: {config.company_id || "Not set"} | Default User ID: {config.default_user_id || "Not set"}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
                              OBD: {config.obd_url || "-"}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            {!config.is_active && (
                              <Button size="small" variant="contained" onClick={() => handleActivateConfig(config)}>
                                Make Active
                              </Button>
                            )}
                            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEditDialog(config)}>
                              Edit
                            </Button>
                            <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => handleDeleteConfig(config._id)}>
                              Delete
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}

                  {activeConfig && (
                    <>
                      <Divider />
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Active credential record: {activeConfig.name}
                      </Typography>
                    </>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingConfig ? "Edit MyOperator Credentials" : "Add MyOperator Credentials"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.2 }}>
            {[
              ["name", "Profile Name"],
              ["company_id", "OBD Voice Company ID"],
              ["public_ivr_id", "OBD Public IVR ID"],
              ["default_user_id", "Default OBD User ID"],
              ["admin_number", "Admin Number"],
              ["api_key", "OBD x-api-key"],
              ["public_api_token", "Public API Token"],
              ["secret_token", "OBD Secret Token"],
              ["webhook_secret", "Webhook Secret"],
              ["obd_url", "OBD URL"],
              ["search_url", "Search URL"],
              ["recording_url", "Recording URL"],
            ].map(([key, label]) => (
              <Grid item xs={12} md={key.includes("url") ? 12 : 6} key={key}>
                <TextField
                  fullWidth
                  label={label}
                  helperText={["company_id", "public_ivr_id", "api_key", "secret_token"].includes(key) ? "Use values from MyOperator OBD/voice calling setup for this account." : ""}
                  value={configForm[key] || ""}
                  onChange={(event) => setConfigForm((prev) => ({ ...prev, [key]: event.target.value }))}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Notes"
                value={configForm.notes}
                onChange={(event) => setConfigForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveConfig}
            disabled={configSaving || !configForm.name.trim()}
          >
            {configSaving ? "Saving..." : editingConfig ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyOperatorSettings;
